import { CSSProperties, Fragment, useEffect, useRef, useState } from 'react';
import { DiagramDef, NodeDef, ScenarioTr } from '../data/projectExtras';
import { useLanguage } from '../i18n/LanguageContext';
import { ACCENT, MONO, WARM, hexA } from './theme';

const accent = ACCENT;
const STEP = 2.1;
const T0 = 0.6;

// Bu genişliğin altında düğümler birbirine giriyor; şema yerine dikey akış gösterilir
const COMPACT_WIDTH = 560;

const pct = (x: number, y: number) => [(x / 8).toFixed(2) + '%', (y / 5).toFixed(2) + '%'];

function edgeGeo(A: NodeDef, B: NodeDef, yv?: number) {
  const [x1, y1] = A;
  const [x2, y2] = B;
  if (yv != null) return { d: `M${x1} ${y1} V${yv} H${x2} V${y2}`, p: [[x1, y1], [x1, yv], [x2, yv], [x2, y2]] };
  if (y1 === y2 || x1 === x2) {
    return {
      d: `M${x1} ${y1} L${x2} ${y2}`,
      p: [[x1, y1], [x1 + (x2 - x1) / 3, y1 + (y2 - y1) / 3], [x1 + (2 * (x2 - x1)) / 3, y1 + (2 * (y2 - y1)) / 3], [x2, y2]],
    };
  }
  const xm = (x1 + x2) / 2;
  return { d: `M${x1} ${y1} H${xm} V${y2} H${x2}`, p: [[x1, y1], [xm, y1], [xm, y2], [x2, y2]] };
}

function buildDiagram(D: DiagramDef, scIndex: number, trs: ScenarioTr | null) {
  const sc = D.scenarios[scIndex] || D.scenarios[0];
  const routes = D.routes || {};
  const geo = (f: string, to: string) => edgeGeo(D.nodes[f], D.nodes[to], routes[`${f}>${to}`] ?? routes[`${to}>${f}`]);

  const seen: Record<string, boolean> = {};
  const edges: { d: string }[] = [];
  D.scenarios.forEach((s) =>
    s.steps.forEach((x) => {
      const k = [x[0], x[1]].sort().join('|');
      if (!seen[k]) {
        seen[k] = true;
        edges.push({ d: geo(x[0], x[1]).d });
      }
    })
  );

  const lit: Record<string, { at: number; kind: string }> = { [sc.steps[0][0]]: { at: 0.25, kind: 'norm' } };
  const active: { d: string; at: string }[] = [];
  const tokens: Record<string, string>[] = [];
  const caps: { n: string; t: string; nc: string; tc: string; anim: string }[] = [];
  sc.steps.forEach((x, i) => {
    const t = T0 + i * STEP;
    const g = geo(x[0], x[1]);
    active.push({ d: g.d, at: t.toFixed(2) + 's' });
    const P = g.p.map((q) => pct(q[0], q[1]));
    tokens.push({ x0: P[0][0], y0: P[0][1], x1: P[1][0], y1: P[1][1], x2: P[2][0], y2: P[2][1], x3: P[3][0], y3: P[3][1], at: t.toFixed(2) + 's' });
    lit[x[1]] = { at: t + 0.95, kind: x[3] || 'norm' };
    const next = T0 + (i + 1) * STEP;
    caps.push({
      n: String(i + 1).padStart(2, '0'),
      t: trs ? trs.caps[i] : x[2],
      nc: accent,
      tc: '#EDEDEB',
      anim: `ftFade .3s ease ${t.toFixed(2)}s both, ftOut .3s ease ${next.toFixed(2)}s forwards`,
    });
  });
  const tEnd = T0 + sc.steps.length * STEP;
  const failed = sc.result[1] === 'fail';
  caps.push({
    n: '→',
    t: trs ? trs.result : sc.result[0],
    nc: failed ? WARM : accent,
    tc: failed ? WARM : accent,
    anim: `ftFade .4s ease ${tEnd.toFixed(2)}s both`,
  });

  const nodes = Object.keys(D.nodes).map((id) => {
    const [x, y, t, s, kind] = D.nodes[id];
    const [left, top] = pct(x, y);
    const L = lit[id];
    const k = L ? L.kind : 'norm';
    return {
      t, s, left, top,
      bs: kind === 'store' ? 'dashed' : 'solid',
      sc: kind === 'guard' ? accent : '#8A8A85',
      lit: !!L,
      at: L ? L.at.toFixed(2) + 's' : '0s',
      lc: k === 'fail' ? WARM : accent,
      lb: k === 'fail' ? hexA(WARM, 0.14) : hexA(accent, k === 'ok' ? 0.22 : 0.1),
    };
  });

  return { edges, active, tokens, caps, nodes, halo: hexA(accent, 0.22) };
}

function HoodFlow({ diagram, scIndex, trs }: { diagram: DiagramDef; scIndex: number; trs: ScenarioTr | null }) {
  const sc = diagram.scenarios[scIndex] || diagram.scenarios[0];
  const node = (id: string) => diagram.nodes[id];
  const failed = sc.result[1] === 'fail';

  return (
    <ol className="hood-flow">
      {sc.steps.map(([from, to, caption, kind], i) => (
        <li key={i} className="hood-flow-step a" style={{ animationDelay: `${0.2 + i * 0.35}s` }}>
          <span className="hood-flow-num">{String(i + 1).padStart(2, '0')}</span>
          <div className="hood-flow-body">
            <div className="hood-flow-path">
              <span className={`hood-chip${node(from)[4] === 'store' ? ' store' : ''}`}>{node(from)[2]}</span>
              <span className="hood-flow-arrow">→</span>
              <span className={`hood-chip lit${kind === 'fail' ? ' fail' : ''}${node(to)[4] === 'store' ? ' store' : ''}`}>
                {node(to)[2]}
              </span>
            </div>
            <p className="hood-flow-text">{trs ? trs.caps[i] : caption}</p>
          </div>
        </li>
      ))}
      <li className={`hood-flow-result a${failed ? ' fail' : ''}`} style={{ animationDelay: `${0.2 + sc.steps.length * 0.35}s` }}>
        <span className="hood-flow-num">→</span>
        <span>{trs ? trs.result : sc.result[0]}</span>
      </li>
    </ol>
  );
}

// Bir isteğin sistem içindeki yolunu adım adım canlandırır. Senaryo seçmek ya da
// "Tekrar oynat" key'i değiştirip alt ağacı yeniden monte eder; böylece CSS
// animasyonları baştan başlar.
function HoodDiagram({ diagram, diagramTr }: { diagram: DiagramDef; diagramTr?: ScenarioTr[] }) {
  const { t, lang } = useLanguage();
  const [sc, setSc] = useState(0);
  const [run, setRun] = useState(0);
  const trOf = (i: number) => (lang === 'tr' && diagramTr ? diagramTr[i] || null : null);
  const dg = buildDiagram(diagram, sc, trOf(sc));
  const ref = useRef<HTMLDivElement>(null);
  const [compact, setCompact] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => setCompact(entry.contentRect.width < COMPACT_WIDTH));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div className="hood" ref={ref}>
      <div className="hood-bar">
        <div role="group" aria-label="Scenario" className="hood-tabs">
          {diagram.scenarios.map((s, i) => (
            <button
              key={i}
              type="button"
              aria-pressed={i === sc}
              className={`hood-tab${i === sc ? ' active' : ''}`}
              onClick={() => {
                setSc(i);
                setRun((r) => r + 1);
              }}
            >
              {trOf(i)?.label ?? s.label}
            </button>
          ))}
        </div>
        <button type="button" className="hood-replay" onClick={() => setRun((r) => r + 1)}>
          {t.work.replay}
        </button>
      </div>
      {compact ? (
        <HoodFlow key={run} diagram={diagram} scIndex={sc} trs={trOf(sc)} />
      ) : (
        <div key={run}>
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 10', containerType: 'inline-size', background: '#111111', overflow: 'hidden' }}>
          <svg viewBox="0 0 800 500" preserveAspectRatio="none" aria-hidden="true" style={{ position: 'absolute', inset: '0', width: '100%', height: '100%' }}>
            {dg.edges.map((e, eI) => (
              <Fragment key={eI}>
                <path d={e.d} fill="none" stroke="#2C2C2C" strokeWidth="1.5" vectorEffect="non-scaling-stroke" />
              </Fragment>
            ))}
            {dg.active.map((e, eI) => (
              <Fragment key={eI}>
                <path className="a" d={e.d} pathLength="1" fill="none" stroke={accent} strokeWidth="2" vectorEffect="non-scaling-stroke" style={{ strokeDasharray: '1', strokeDashoffset: '1', animation: `ftDraw 1s ease-in-out ${e.at} forwards` }} />
              </Fragment>
            ))}
          </svg>
          {dg.tokens.map((k, kI) => (
            <Fragment key={kI}>
              <span className="a" style={{ position: 'absolute', zIndex: '2', width: '1.3cqw', height: '1.3cqw', margin: '-.65cqw 0 0 -.65cqw', borderRadius: '50%', background: accent, boxShadow: `0 0 0 .45cqw ${dg.halo}`, opacity: '0', left: k.x0, top: k.y0, '--x0': k.x0, '--y0': k.y0, '--x1': k.x1, '--y1': k.y1, '--x2': k.x2, '--y2': k.y2, '--x3': k.x3, '--y3': k.y3, animation: `ftTok 1s ease-in-out ${k.at} both` } as CSSProperties}></span>
            </Fragment>
          ))}
          {dg.nodes.map((n, nI) => (
            <Fragment key={nI}>
              <div style={{ position: 'absolute', zIndex: '3', left: n.left, top: n.top, width: '15.5cqw', transform: 'translate(-50%, -50%)' }}>
                <div style={{ position: 'relative', background: '#161616', border: `1px ${n.bs} #3A3A3A`, borderRadius: '1cqw', padding: '.9cqw 1.1cqw', display: 'flex', flexDirection: 'column', gap: '.25cqw' }}>
                  {n.lit && (
                    <span className="a" style={{ position: 'absolute', inset: '-1px', borderRadius: '1cqw', border: `1.5px solid ${n.lc}`, background: n.lb, animation: `ftLitIn .45s ease ${n.at} both` }}></span>
                  )}
                  <span style={{ position: 'relative', fontSize: 'max(1.6cqw, 11px)', fontWeight: '500', lineHeight: '1.2' }}>{n.t}</span>
                  <span style={{ position: 'relative', fontFamily: MONO, fontSize: 'max(1.12cqw, 9px)', lineHeight: '1.3', color: n.sc }}>{n.s}</span>
                </div>
              </div>
            </Fragment>
          ))}
        </div>
        <div style={{ position: 'relative', minHeight: '76px', borderTop: '1px solid #262626' }}>
          {dg.caps.map((c, cI) => (
            <Fragment key={cI}>
              <div className="a" style={{ position: 'absolute', left: '20px', right: '20px', top: '16px', display: 'flex', gap: '12px', alignItems: 'baseline', animation: c.anim }}>
                <span style={{ fontFamily: MONO, fontSize: '12px', color: c.nc, flexShrink: '0' }}>{c.n}</span>
                <span style={{ fontSize: '15px', lineHeight: '1.5', color: c.tc }}>{c.t}</span>
              </div>
            </Fragment>
          ))}
        </div>
    
        </div>
      )}
      <div className="hood-stack">
        <span>{t.work.stackLbl}</span>
        <span className="hood-stack-value">{diagram.stack}</span>
      </div>
    </div>
  );
}

export default HoodDiagram;
